import { EmptyValue } from '@/components/common/EmptyValue';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getInitialsFromName } from '@/utils/getInitialsFromName';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { FEEDBACK_STATUS_LABELS, FEEDBACK_STATUS_TONE } from '../constants/feedback.constants';
import type { FeedbackListItem } from '../types/feedback.types';

interface FeedbackContextCardProps {
  feedback: FeedbackListItem;
}

export function FeedbackContextCard({ feedback }: FeedbackContextCardProps) {
  const initials = getInitialsFromName(feedback.user?.fullName);

  return (
    <div className="rounded-xl border bg-muted/30 p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="size-10 shrink-0">
            <AvatarFallback className={cn('text-sm font-semibold', ACCENT_MARK_CLASS)}>
              {initials || EMPTY_VALUE}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {feedback.user?.fullName ?? <EmptyValue />}
            </p>
            {feedback.user?.hrEmpId && (
              <p className="text-xs text-muted-foreground truncate">{feedback.user.hrEmpId}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusBadge tone={FEEDBACK_STATUS_TONE[feedback.status]}>
            {FEEDBACK_STATUS_LABELS[feedback.status]}
          </StatusBadge>
          {feedback.type && (
            <Badge variant="outline" className="font-normal">
              {feedback.type.name}
            </Badge>
          )}
        </div>
      </div>

      <Separator />

      <p className="text-sm leading-relaxed whitespace-pre-wrap min-h-24 max-h-40 overflow-y-auto">
        {feedback.message}
      </p>
    </div>
  );
}
